import { hasValidLearningOutcomes } from './normalizeLearningOutcomes';

export function hasValidCoreOutcomesData(data, hasActualCoreMessage = false) {
  if (!data) return false;
  if (hasActualCoreMessage) return true;
  if (data.core_message && String(data.core_message).trim()) return true;
  const outcomes = Array.isArray(data.outcomes) ? data.outcomes : [];
  return outcomes.some(
    (o) => o?.core_message && String(o.core_message).trim().length > 0
  );
}

export function hasValidReframesData(data) {
  if (!data) return false;
  const reframes = Array.isArray(data.reframes)
    ? data.reframes
    : Array.isArray(data)
      ? data
      : [];
  return reframes.some((rf) => {
    if (!rf) return false;
    if (rf.wording?.statement && String(rf.wording.statement).trim()) return true;
    if (rf.statement && String(rf.statement).trim()) return true;
    if (rf.concept?.concept_summary && String(rf.concept.concept_summary).trim()) return true;
    return false;
  });
}

export function hasValidStructureData(data) {
  if (!data) return false;
  const chapters = data.chapters || data.content_chapters || [];
  if (Array.isArray(chapters) && chapters.length > 0) return true;
  if (Array.isArray(data.contents) && data.contents.length > 0) return true;
  if (data.content_structure && typeof data.content_structure === 'object' && Object.keys(data.content_structure).length > 0) {
    return true;
  }
  if (data.structure) {
    const nested = Array.isArray(data.structure) ? data.structure : [data.structure];
    if (nested.length > 0) return true;
  }
  if (Array.isArray(data.learning_pathways) && data.learning_pathways.length > 0) return true;
  return Boolean(data.description && String(data.description).trim().length > 0);
}

export function hasValidCompetenciesData(data) {
  if (!data) return false;
  const competencies = data.competencies || (Array.isArray(data) ? data : []);
  return Array.isArray(competencies) && competencies.length > 0;
}

export function isEloNodeData(data) {
  if (!data) return false;
  return !(data.is_orientation || data.is_eisa || data.uses_curriculum_doc);
}

/** Count completed tabs for one ELO module (5 tabs total). */
export function countEloModuleTabs(data = {}) {
  const total = 5;
  const tabChecks = {
    coreMessage: hasValidCoreOutcomesData(data.ai_core_outcomes),
    learningOutcomes: hasValidLearningOutcomes(data.ai_learning_outcomes),
    reframes: hasValidReframesData(data.ai_reframes),
    structure: hasValidStructureData(data.ai_structure),
    competencies: hasValidCompetenciesData(data.ai_competencies)
  };
  const completed = Object.values(tabChecks).filter(Boolean).length;
  return { completed, total, tabChecks, isComplete: completed === total };
}

/** Aggregate progress across all ELO modules in the storyboard. */
export function countOverallStoryboardProgress(nodes = []) {
  let completedTabs = 0;
  let totalTabs = 0;
  const modules = [];

  nodes.forEach((node) => {
    const d = node.data || {};
    if (!isEloNodeData(d)) return;
    const tabCount = countEloModuleTabs(d);
    completedTabs += tabCount.completed;
    totalTabs += tabCount.total;
    modules.push({
      id: node.id,
      title: d.title || node.label || `Module ${d._moduleIndex || '?'}`,
      completed: tabCount.completed,
      total: tabCount.total,
      tabChecks: tabCount.tabChecks,
      isComplete: tabCount.isComplete
    });
  });

  const percent = totalTabs > 0 ? Math.round((completedTabs / totalTabs) * 100) : 0;
  return { completed: completedTabs, total: totalTabs, percent, modules, isComplete: totalTabs > 0 && completedTabs === totalTabs };
}

/** Find the next ELO module (by canvas order) that still has missing tabs. */
export function findNextIncompleteEloModule(nodes = [], afterNodeId = null) {
  const afterIndex = afterNodeId != null ? nodes.findIndex((n) => n.id === afterNodeId) : -1;

  const scan = (from, to) => {
    for (let i = from; i < to; i++) {
      const node = nodes[i];
      if (!node) continue;
      const d = node.data || {};
      if (!isEloNodeData(d)) continue;
      if (!countEloModuleTabs(d).isComplete) return node;
    }
    return null;
  };

  const after = scan(afterIndex + 1, nodes.length);
  if (after) return after;
  return scan(0, afterIndex >= 0 ? afterIndex + 1 : nodes.length);
}

export function isEloModuleComplete({
  coreOutcomesData,
  learningOutcomesData,
  reframesData,
  structureData,
  competenciesData,
  hasActualCoreMessage
}) {
  return (
    hasValidCoreOutcomesData(coreOutcomesData, hasActualCoreMessage) &&
    hasValidLearningOutcomes(learningOutcomesData) &&
    hasValidReframesData(reframesData) &&
    hasValidStructureData(structureData) &&
    hasValidCompetenciesData(competenciesData)
  );
}

export { hasValidLearningOutcomes };
